/**
 * One agent's long view (spec 6.1 "My Overall Performance", 6.2 "Agent Selector"): the same
 * numbers for the agent on their home page and for a manager choosing them from a list.
 */
import { toUsDate, type AgentOverall, type PolicySummary } from "@tlcrm/shared";

import { ChartCard, LineChart, Stat } from "../../components/charts";
import { compactMoney, monthLabel, percent } from "../../lib/chart";
import { DataTable, type Column } from "../../components/DataTable";
import { money } from "../../lib/format";
import { dealColumns } from "./dealColumns";

const usd = (n: number) => money(n);
const cents = (n: number) => money(n, true);

/** Salesforce's tile thresholds (spec 6.2): commission red under $5K, deductions green under $2K. */
const COMMISSION_FLOOR = 5000;
const DEDUCTION_CEILING = 2000;

export function DealTable({
  title,
  deals,
  columns,
  empty,
}: {
  title: string;
  deals: PolicySummary[];
  columns: Column<PolicySummary>[];
  empty: string;
}) {
  return (
    <section className="card">
      <div className="card-head">
        <h3>{title}</h3>
        <span className="mono t3">{deals.length}</span>
      </div>
      <DataTable
        columns={columns}
        rows={deals}
        href={(p) => `/policies/${p.id}`}
        empty={{ icon: "briefcase", title: empty }}
      />
    </section>
  );
}

export function Scorecard({ overall, full }: { overall: AgentOverall; full: boolean }) {
  const o = overall;
  const commissionOk = o.thisMonth.commissionAfterCancels >= COMMISSION_FLOOR;
  const deductionsOk = o.thisMonth.deductions < DEDUCTION_CEILING;
  return (
    <div className="stack">
      <div className={`stat-row ${full ? "six" : "five"}`}>
        <Stat
          label="Cancellation rate"
          value={percent(o.cancellationRate)}
          sub={`${percent(o.cancellationPremiumRate)} of premium`}
        />
        <Stat
          label="All-time sales"
          value={compactMoney(o.allTimePremium)}
          sub={`${compactMoney(o.allTimeNetPremium)} net of cancels`}
        />
        <Stat
          label="Commission this month"
          value={compactMoney(o.thisMonth.commissionAfterCancels)}
          standing={commissionOk ? "good" : "bad"}
          standingWord={commissionOk ? "Over $5K" : "Under $5K"}
          sub="after cancels"
        />
        <Stat
          label="Deductions this month"
          value={compactMoney(o.thisMonth.deductions)}
          standing={deductionsOk ? "good" : "bad"}
          standingWord={deductionsOk ? "Under $2K" : "$2K or more"}
        />
        <Stat
          label="Previous month sales"
          value={compactMoney(o.previousMonth.premium)}
          sub={`${compactMoney(o.previousMonth.premiumUnder2500)} under $2,500`}
        />
        {full && (
          <Stat
            label="Previous month cancels"
            value={o.previousMonth.cancels + o.previousMonth.notTaken}
            sub={`${o.previousMonth.cancels} cancelled · ${o.previousMonth.notTaken} not taken`}
          />
        )}
      </div>
      <div className="dash-grid">
        <div className="span-4">
          <ChartCard
            title="Weekly production"
            sub="Closed Won premium by week of Date Sold, this year"
            table={{
              columns: ["Week of", "Premium"],
              rows: o.weeklyProduction.map((p) => [toUsDate(p.date), cents(p.value)]),
            }}
          >
            <LineChart
              points={o.weeklyProduction}
              format={usd}
              axisFormat={compactMoney}
              ariaLabel="Weekly production"
            />
          </ChartCard>
        </div>
        <div className="span-4">
          <ChartCard
            title="Monthly commission"
            sub="Expected Commission after cancels, by month sold"
            table={{
              columns: ["Month", "Commission"],
              rows: o.monthlyCommission.map((p) => [monthLabel(p.date), cents(p.value)]),
            }}
          >
            <LineChart
              points={o.monthlyCommission}
              format={usd}
              axisFormat={compactMoney}
              label={monthLabel}
              ariaLabel="Monthly commission"
            />
          </ChartCard>
        </div>
        <div className="span-4">
          <ChartCard
            title="Monthly deductions"
            sub="Charged back, by month sold"
            table={{
              columns: ["Month", "Deducted"],
              rows: o.monthlyDeductions.map((p) => [monthLabel(p.date), cents(p.value)]),
            }}
          >
            <LineChart
              points={o.monthlyDeductions}
              format={usd}
              axisFormat={compactMoney}
              label={monthLabel}
              ariaLabel="Monthly deductions"
            />
          </ChartCard>
        </div>
      </div>
      <div className="dash-grid">
        <div className="span-6">
          <DealTable
            title="Canceled / not taken deals"
            deals={o.cancelledDeals}
            columns={[
              dealColumns.customer,
              dealColumns.premium,
              dealColumns.carrier,
              dealColumns.cancelled,
              dealColumns.reason,
            ]}
            empty="No cancelled or not-taken policies"
          />
        </div>
        <div className="span-6">
          <DealTable
            title="Unpaid deals"
            deals={o.unpaidDeals}
            columns={[
              dealColumns.policy,
              dealColumns.premium,
              dealColumns.carrier,
              dealColumns.dateSold,
              dealColumns.effective,
            ]}
            empty="Every Closed Won policy is paid"
          />
        </div>
        {full && (
          <>
            <div className="span-6">
              <DealTable
                title="Pending deals"
                deals={o.pendingDeals}
                columns={[
                  dealColumns.customer,
                  dealColumns.premium,
                  dealColumns.callType,
                  dealColumns.dateSold,
                  dealColumns.effective,
                ]}
                empty="Nothing waiting on a first draft"
              />
            </div>
            <div className="span-6">
              <DealTable
                title="Rewrite deals"
                deals={o.rewriteDeals}
                columns={[dealColumns.policy, dealColumns.premium, dealColumns.dateSold]}
                empty="No rewrites"
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
}
